import { type FutureEventEntity } from '@solink/vd-data-layer/api-clients/platform/platform';
import { useUserSession } from '@solinkcorp/authentication';
import { toast, toastUnexpectedError } from '@solinkcorp/solink-components';
import Browsers from '@solinkcorp/utils/browsers';
import * as ContextineActions from 'actions/ContextineActions';
import EventActions from 'actions/EventActions';
import { CloudVmsApi } from 'clients';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type IEvent } from 'clients/PlatformApi';
import {
  useArchivePanel,
  type UseArchivePanelProps,
} from 'components/ArchivePanel/useArchivePanel';
import { CommonContext } from 'components/Contexts';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { usePrintPanel, type UsePrintPanelProps } from 'components/PrintPanel/usePrintPanel';
import { useSavePanel, type UseSavePanelProps } from 'components/SavePanel/useSavePanel';
import { useScheduledActivity } from 'components/ScheduledActivityForm/useScheduledActivity';
import {
  type ScheduledEventFormProps,
  useScheduledEventForm,
} from 'components/ScheduledActivityForm/useScheduledEventForm';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { useCallback, useContext, useRef } from 'react';
import { useControllerState } from 'solox';
import useDevicesFromSite from 'src/hooks/devices/useDevicesFromSite';
import { useCurrentFeature } from 'src/hooks/useEventFeature';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import EventFilterStore from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import { useSelector } from 'stores/store';
import { type KeyedMutator } from 'swr';
import CommonUtil from 'utils/CommonUtil';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import isShallowEqual from 'utils/isShallowEqual';
import { EVENT_STATUS_OPEN, FAST_NOTIFICATION_DURATION, SHARE_STATUS_ENABLED } from 'vd-constants';
import { buildEventShareUrl } from './buildEventShareUrl';
import useClipViewShareLink from './useClipViewShareLink';

interface UseEventMenuActions {
  event: IEvent;
  isAlarms?: boolean;
  isClips?: boolean;
  updateEvent?: (newEvent: IEvent) => void;
  removeEvent?: (targetEvent: IEvent) => void;
}
export interface EventMenuActionsProps {
  handleOpenSavePanel: ({
    saveMode,
    saveExistingEvent,
  }: {
    saveMode: boolean;
    saveExistingEvent: boolean;
  }) => void;
  handleOpenArchivePanel: ({ type }: { type: 'archive' | 'unarchive' }) => void;
  handleOpenPrintPanel: () => void;
  savePanelProps: UseSavePanelProps | undefined;
  archivePanelProps: UseArchivePanelProps | undefined;
  printPanelProps: UsePrintPanelProps | undefined;
  scheduledEventFormProps: ScheduledEventFormProps | undefined;
  viewShare: () => void;
  handleCopyShareLink: () => void;
  getShareUrl: () => string;
  handleContextine: () => void;
  handleCopyLink: () => void;
  scheduledActivity: ScheduledActivityEntity | undefined;
  invalidateScheduledActivity: KeyedMutator<ScheduledActivityEntity | undefined>;
  openScheduledEventForm: () => void;
  event: IEvent;
  playingCameras: PlayingCamera[];
  updateEvent?: (newEvent: IEvent) => void;
  removeEvent?: (targetEvent: IEvent) => void;
  additionalArchiveActions: () => void;
  additionalUnarchiveActions: () => void;
  isSaveClipMenuVisible: boolean;
  /** When true, "Add to Investigation" posts kind: 'clip' instead of 'event'. */
  isClips?: boolean;
}

export default function useEventMenuActions({
  event,
  isAlarms,
  isClips,
  updateEvent,
  removeEvent,
}: UseEventMenuActions): EventMenuActionsProps {
  const { openSavePanel, savePanelProps } = useSavePanel();
  const { openArchivePanel, archivePanelProps } = useArchivePanel();
  const { openPrintPanel, printPanelProps } = usePrintPanel();

  const scheduledActivityId = event.details?.scheduledActivity?.reoccurringId;

  const feature = useCurrentFeature();

  const { customerId: tenantId, s3 } = useUserSession();

  const { data: scheduledActivity, mutate: invalidateScheduledActivity } =
    useScheduledActivity(scheduledActivityId);

  const { openScheduledEventForm, scheduledEventFormProps, cancelScheduledEvent } =
    useScheduledEventForm(event as FutureEventEntity);

  const locale = useLocale();
  const { range: histogramRange, locationIds } = useSelector((state) => state.filter);
  const camerasDictionary = useFluxStore(LocationStore, (store) =>
    store.getAllLocationCamerasDict(),
  );

  const { devices: firstSitesDevices } = useDevicesFromSite(locationIds?.[0] ?? '');

  const {
    capsules: searchCapsules,
    eventFilterState,
    searchString,
    showRevenue,
    searchOperator,
  } = useFluxStore(EventFilterStore, (store) => store.getAppState(), isShallowEqual);

  const selectedReportId = useFluxStore(EventFilterStore, (store) =>
    store.getSelectedReportId({ checkInitial: true }),
  );

  const {
    authData: { contextineCreds },
  } = useContext(CommonContext);

  const playingCamerasController = usePlayingCamerasContext();

  const playingCameras = useControllerState(playingCamerasController.state, () =>
    playingCamerasController.getPlayingCameras(),
  );

  const browsers = useRef(new Browsers());
  const isSafari = browsers.current.isSafari;

  /*
   * Only fetches the link on component mount when browser is Safari.
   * Safari has a limitation that it only allows copying to clipboard within
   * user events, like click or pointerdown.
   * So for Safari, we pre - fetch the share links for the clips that are in view
   * (there aren't too many thanks to our recycler list mechanism).
   * For other browsers, after the user clicks the button, we make an API call
   * to get the URL first then in the callback we copy the URL to the clipboard.
   * https://webkit.org/blog/10247/new-webkit-features-in-safari-13-1
   */
  const { clipViewShareLink: clipViewShareLinkForSafari } = useClipViewShareLink(event, isSafari);

  const getClipShareLink = useCallback(async () => {
    const isShared =
      EventUtil.isShared(event) &&
      (EventUtil.isPushToCloudComplete(event) || EventUtil.isPushToCloudVerify(event));

    if (isShared) {
      const { id: eventId, shares } = event;
      const selfShare = shares?.find((share) => {
        const { sharedBy, email } = share;
        return sharedBy === email;
      });

      if (selfShare) {
        const { id: shareId } = selfShare;
        try {
          const response = await CloudVmsApi().getSharedViewLink({
            eventId,
            shareId: shareId as string,
          });
          return response.url;
        } catch (error) {
          console.warn(`Error getting shared view link: ${error}`);
        }
      }
    }
  }, [event]);

  const handleOpenSavePanel = useCallback(
    ({ saveMode, saveExistingEvent }: { saveMode: boolean; saveExistingEvent: boolean }) => {
      AnalyticsLogger.clickEventMenuShare({ event });

      openSavePanel(event, saveMode, saveExistingEvent);
    },
    [openSavePanel, event],
  );

  const handleOpenArchivePanel = useCallback(
    ({ type }: { type: 'archive' | 'unarchive' }) => {
      AnalyticsLogger.clickEventMenuShare({ event });

      openArchivePanel(event, type);
    },
    [openArchivePanel, event],
  );

  const handleOpenPrintPanel = useCallback(() => {
    openPrintPanel(event);
  }, [openPrintPanel, event]);

  const openSharePageInNewWindow = useCallback(
    ({ link, share }: { link: string; share: { [key: string]: any } }) => {
      const { id: shareId, sharedBy } = share;
      window.open(link, '_blank');
      AnalyticsLogger.clickViewSharePage({ event, shareId, sharedBy });
    },
    [event],
  );

  const viewShare = useCallback(async () => {
    const selfShare = event.shares?.find((share) => {
      const { sharedBy, email } = share;
      return sharedBy === email;
    });

    const shareUrl = clipViewShareLinkForSafari ?? (await getClipShareLink());

    if (selfShare && shareUrl) {
      openSharePageInNewWindow({ link: shareUrl, share: selfShare });
    } else {
      toast.error(locale.share.unableToCopyToClipboard, { autoClose: FAST_NOTIFICATION_DURATION });
    }
  }, [event, getClipShareLink, locale, openSharePageInNewWindow, clipViewShareLinkForSafari]);

  const copyLinkSuccess = useCallback(() => {
    toast.success(locale.share.copiedToClipboard, {
      autoClose: FAST_NOTIFICATION_DURATION,
    });
  }, [locale]);

  const copyLinkError = useCallback(() => {
    toast.error(locale.share.unableToCopyToClipboard, {
      autoClose: FAST_NOTIFICATION_DURATION,
    });
  }, [locale]);

  const copyLink = useCallback(
    (link: string) => {
      CommonUtil.copyStringToClipboard({
        string: link,
        successCallback: copyLinkSuccess,
        errorCallback: copyLinkError,
      });
    },
    [copyLinkError, copyLinkSuccess],
  );

  const handleCopyShareLink = useCallback(async () => {
    const shareUrl = clipViewShareLinkForSafari ?? (await getClipShareLink());
    if (shareUrl) {
      copyLink(shareUrl);
    } else {
      toast.error(locale.share.unableToCopyToClipboard, { autoClose: FAST_NOTIFICATION_DURATION });
    }
  }, [getClipShareLink, locale, copyLink, clipViewShareLinkForSafari]);

  const getShareUrl = useCallback(() => {
    return buildEventShareUrl({
      event,
      origin: window.location.origin,
      tenantId,
      locationIds,
      // locationId = deviceId, used to support old mobile apps
      firstSiteFirstDeviceId: firstSitesDevices?.[0]?.id,
      isAlarms,
      isClips,
      eventFilterState,
      searchString,
      showRevenue,
      searchCapsules,
      selectedReportId,
      histogramRange,
      feature,
      searchOperator,
    });
  }, [
    event,
    isAlarms,
    isClips,
    tenantId,
    locationIds,
    eventFilterState,
    searchString,
    showRevenue,
    searchCapsules,
    selectedReportId,
    histogramRange,
    feature,
    searchOperator,
    firstSitesDevices,
  ]);

  const handleCopyLink = useCallback(() => {
    const url = getShareUrl();
    copyLink(url);
    AnalyticsLogger.clickEventMenuCopy({ url });
  }, [getShareUrl, copyLink]);

  const handleContextine = useCallback(async () => {
    const url = clipViewShareLinkForSafari ?? (await getShareUrl());
    const win = window.open('https://contextine-loading.solinkcloud.com');

    // TODO: Wat?
    if (win) {
      (window as any).oldOpen = window.open;
      (window as any).open = (url: string) => {
        // reassignment ftn
        if (url) {
          win.location.href = url;
        }
        window.open = (window as any).oldOpen;
        win.focus();
      };
    }

    if (!s3) {
      toast.error(locale.share.errorSharingToContextine, { autoClose: FAST_NOTIFICATION_DURATION });
      return;
    }

    if (url) {
      ContextineActions.shareEvent(playingCameras, url, event, contextineCreds, tenantId, s3)
        .then((res) => {
          if (res.success) {
            toast.success(locale.share.sharedToContextine, {
              autoClose: FAST_NOTIFICATION_DURATION,
            });
            // launch case in new window
            const url = res.data;
            window.open(url);
          } else {
            const message = locale.share.errorSharingToContextine;
            toast.error(message, { autoClose: FAST_NOTIFICATION_DURATION });
            console.error(message);
          }
          // TODO: need to trigger the global notification when we have it
        })
        .catch(toastUnexpectedError);
    }
  }, [
    clipViewShareLinkForSafari,
    getShareUrl,
    s3,
    locale.share.errorSharingToContextine,
    locale.share.sharedToContextine,
    playingCameras,
    event,
    contextineCreds,
    tenantId,
  ]);

  const closeScheduledEvent = useCallback(() => {
    return cancelScheduledEvent({
      event: event as FutureEventEntity,
      updateEvent,
      successMessage: locale.event.scheduledCanceled,
    });
  }, [cancelScheduledEvent, event, locale, updateEvent]);

  const additionalArchiveActions = useCallback(() => {
    if (EventUtil.isScheduledActivity(event) && event.state === EVENT_STATUS_OPEN) {
      closeScheduledEvent();
    }
  }, [event, closeScheduledEvent]);

  const additionalUnarchiveActions = useCallback(() => {
    if (event.shares && EventUtil.isShared(event)) {
      const share = event.shares.find(({ email, sharedBy }) => sharedBy === email);

      if (share) {
        const restoredShare = { id: share.id, status: SHARE_STATUS_ENABLED };
        EventActions.editEvent(event.id, { shares: [restoredShare] }).catch((err: string) => {
          toast.error(err, {
            autoClose: FAST_NOTIFICATION_DURATION,
          });
        });
      }
    }
  }, [event]);

  const isScheduled = EventUtil.isScheduledActivity(event);
  const isInFuture = EventUtil.isInFuture(event);
  const isEditAllowed = !EventUtil.isEventIdInvalid(event.id);
  const isSaveClipMenuVisible =
    isEditAllowed &&
    FeaturesUtil.canShareToCloud() &&
    FeaturesUtil.hasPublicSharesEnabled() &&
    EventUtil.isShareable(event) &&
    EventUtil.hasCameras(event) &&
    EventUtil.hasCallhomeCameras(event, camerasDictionary) &&
    (!isScheduled || (isScheduled && !isInFuture));

  return {
    savePanelProps,
    archivePanelProps,
    printPanelProps,
    scheduledEventFormProps,
    handleOpenSavePanel,
    handleOpenArchivePanel,
    handleOpenPrintPanel,
    viewShare,
    handleCopyShareLink,
    getShareUrl,
    handleContextine,
    handleCopyLink,
    scheduledActivity,
    invalidateScheduledActivity,
    openScheduledEventForm,
    event,
    playingCameras,
    updateEvent,
    removeEvent,
    additionalArchiveActions,
    additionalUnarchiveActions,
    isSaveClipMenuVisible,
    isClips,
  };
}
